/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from '@e3dc-react/shell/Libs/moment'
import { createStore, combineReducers, Store, EmptyObject } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import { persistStore, persistReducer, PersistPartial, MigrationManifest, createMigrate, PersistConfig, Persistor } from 'redux-persist'
import createCompressor from 'redux-persist-transform-compress'
import { createTransform } from 'redux-persist'
import {
  Breakthrough,
  BreakthroughState,
  ErrorState,
  FetchingState,
  FetchState,
  ForecastPower,
  ForecastState,
  HistoryData,
  HistoryState,
  SystemInfo,
  SystemInfoState,
  VersionState,
} from '../models/systemDataStoreModel'
import { isStartOfMonth } from '../utils/datetime'
import { WallboxHistory } from '../../../Types/responses/wallboxes/{sn}'
import { PowermeterHistory } from '../../../Types/responses/powermeter-values/{sn}'
import objectApiResult, { revertObjectApiResult } from '@e3dc-react/shell/Libs/ApiHelpers/objectApiResult'
import { isUndefined } from 'lodash'

export interface SystemDataStoreAction {
  type:
    | 'systemInfo/fetched'
    | 'systemInfo/fetching'
    | 'history/fetched'
    | 'history/fetching'
    | 'forecast/power'
    | 'breakthrough'
    | 'calculatedData'
    | 'error'
    | 'fetched/historyData/extendedSet'
    | 'fetched/historyData/defaultSet'
    | 'reset'
  serialNo: string
  systemInfo?: SystemInfo
  historyData?: HistoryData[]
  wallboxes?: WallboxesHistoryData
  powermeters?: PowermeterHistoryData
  completed?: boolean
  forecastPower?: ForecastPower[]
  breakthrough?: Breakthrough[]
  error?: Error
}

interface FetchingStateAction {
  type: 'history/fetched' | 'history/fetching' | 'reset'
  serialNo: string
  from: number
}

export interface VersionStateAction {
  type: 'version'
  version?: string
}

export interface SystemDataStoreActionWithTime extends SystemDataStoreAction {
  from: number
}

function systemInfo(state: SystemInfoState = {}, action: SystemDataStoreAction): SystemInfoState {
  switch (action.type) {
    case 'systemInfo/fetched':
      return {
        ...state,
        [action.serialNo]: {
          data: action.systemInfo,
          state: 'FETCHED',
          currentTs: moment().valueOf(),
        },
      }
    case 'systemInfo/fetching':
      return {
        ...state,
        [action.serialNo]: {
          data: state[action.serialNo]?.data,
          state: 'FETCHING',
          currentTs: moment().valueOf(),
        },
      }
    default:
      return state
  }
}

const versionInfo = (state: VersionState = {}, action: VersionStateAction): VersionState => {
  switch (action.type) {
    case 'version':
      return {
        ...state,
        version: action.version,
      }
    default:
      return state
  }
}
export const updateAppVersion = (version: string): VersionStateAction => ({ type: 'version', version })

type WallboxesHistoryData = Record<string, WallboxHistory[]> | undefined
type PowermeterHistoryData = Record<string, PowermeterHistory[]> | undefined

const getWallboxHistoryData = (wallboxes: WallboxesHistoryData, actionWallboxes: WallboxesHistoryData): WallboxesHistoryData => {
  const wallboxHistoryData: WallboxesHistoryData = {}

  /* handle new wallboxes data */
  if (actionWallboxes) {
    Object.keys(actionWallboxes).forEach(wallboxKey => {
      const actionWallboxData = actionWallboxes[wallboxKey]
      const wallboxHistory = wallboxes?.[wallboxKey]

      /* if the wallbox was not in store, add it with these values */
      if (isUndefined(wallboxHistory)) {
        wallboxHistoryData[wallboxKey] = actionWallboxData
      } else {
        /* add only missing wallbox data to the store */
        const lastTs = wallboxHistory[wallboxHistory.length - 1]?.time
        const filteredWallboxData = actionWallboxData.filter(data => data?.time > lastTs) ?? []
        wallboxHistoryData![wallboxKey] = [...wallboxHistory, ...filteredWallboxData]
      }
    })
  }
  return wallboxHistoryData
}

const getPowermeterHistoryData = (powermeters: PowermeterHistoryData, actionPowermeters: PowermeterHistoryData): PowermeterHistoryData => {
  const powermeterHistoryData: PowermeterHistoryData = {}

  /* handle new powermeters data */
  if (actionPowermeters) {
    Object.keys(actionPowermeters).forEach(powermeterKey => {
      const actionPowermeterData = actionPowermeters[powermeterKey]
      const powermeterHistory = powermeters?.[powermeterKey]

      /* if the powermeter was not in store, add it with these values */
      if (isUndefined(powermeterHistory)) {
        powermeterHistoryData[powermeterKey] = actionPowermeterData
      } else {
        /* add only missing powermeter data to the store */
        const lastTs = powermeterHistory[powermeterHistory.length - 1]?.time
        const filteredPowermeterData = actionPowermeterData.filter(data => data?.time > lastTs) ?? []
        powermeterHistoryData[powermeterKey] = [...powermeterHistory, ...filteredPowermeterData]
      }
    })
  }

  return powermeterHistoryData
}

function historyData(state: HistoryState = {}, action: SystemDataStoreActionWithTime): HistoryState {
  switch (action.type) {
    // case 'history/fetched':
    case 'fetched/historyData/extendedSet':
      //eslint-disable-next-line
      let fromKey: number
      //eslint-disable-next-line
      let data: HistoryData[]
      // eslint-disable-next-line no-case-declarations
      let wallboxData: WallboxesHistoryData
      // eslint-disable-next-line no-case-declarations
      let powermeterData: PowermeterHistoryData

      if (isStartOfMonth(moment(action.from))) {
        fromKey = action.from
        data = action.historyData ?? []
        wallboxData = action.wallboxes
        powermeterData = action.powermeters
      } else {
        fromKey = moment(action.from).startOf('month').valueOf()
        const oldData = state[action.serialNo][fromKey].data
        const lastTs = oldData[oldData.length - 1].time
        const wallboxes = state[action.serialNo][fromKey].wallboxes
        const powermeters = state[action.serialNo][fromKey].powermeters
        data = [...oldData, ...(action.historyData?.filter(data => data.time > lastTs) ?? [])]
        powermeterData = getPowermeterHistoryData(powermeters, action.powermeters)
        wallboxData = getWallboxHistoryData(wallboxes, action.wallboxes)
      }

      return {
        ...state,
        [action.serialNo]: {
          ...state[action.serialNo],
          [fromKey]: {
            // [action.from]: {
            data,
            // data: action.historyData,
            wallboxes: wallboxData,
            powermeters: powermeterData,
            state: 'FETCHED',
            completed: !!action.completed,
            first: data[0]?.time ? moment(data[0]?.time).toString() : undefined,
            last: data[data.length - 1]?.time ? moment(data[data.length - 1]?.time).toString() : undefined,
          },
        },
      }
    case 'fetched/historyData/defaultSet': {
      let fromKey
      let data

      if (isStartOfMonth(moment(action.from))) {
        fromKey = action.from
        data = action.historyData ?? []
      } else {
        fromKey = moment(action.from).startOf('month').valueOf()
        const oldData = state[action.serialNo][fromKey]?.data || []
        const lastTs = oldData[oldData.length - 1]?.time || 0
        data = [...oldData, ...(action.historyData?.filter(data => data.time > lastTs) ?? [])]
      }

      return {
        ...state,
        [action.serialNo]: {
          ...state[action.serialNo],
          [fromKey]: {
            data,
            state: 'FETCHED',
            completed: !!action.completed,
            first: data[0]?.time ? moment(data[0]?.time).toString() : undefined,
            last: data[data.length - 1]?.time ? moment(data[data.length - 1]?.time).toString() : undefined,
          },
        },
      }
    }
    case 'reset': {
      return {}
    }
    // case 'history/fetching':
    //   return {
    //     ...state,
    //     [action.serialNo]: {
    //       ...state[action.serialNo],
    //       [action.from]: {
    //         ...state[action.serialNo]?.[action.from],
    //         state: 'FETCHING',
    //         completed: false,
    //       },
    //     },
    //   }
    default:
      return state
  }
}

function forecastPower(state: ForecastState = {}, action: SystemDataStoreActionWithTime): ForecastState {
  switch (action.type) {
    case 'fetched/historyData/extendedSet':
      // case 'forecast/power':
      return {
        ...state,
        [action.serialNo]: {
          ...state[action.serialNo],
          [action.from]: action.forecastPower ?? [],
        },
      }
    case 'reset': {
      return {}
    }
    default:
      return state
  }
}

function breakthrough(state: BreakthroughState = {}, action: SystemDataStoreActionWithTime): BreakthroughState {
  switch (action.type) {
    // case 'breakthrough':
    case 'fetched/historyData/extendedSet':
      return {
        ...state,
        [action.serialNo]: {
          ...state[action.serialNo],
          [action.from]: action.breakthrough ?? [],
        },
      }
    default:
      return state
  }
}

function systemDataError(state: ErrorState = {}, action: SystemDataStoreAction): ErrorState {
  switch (action.type) {
    case 'error':
      return {
        ...state,
        [action.serialNo]: action.error,
      }
    default:
      return state
  }
}

function fetchState(state: FetchState = {}, action: FetchingStateAction): FetchState {
  switch (action.type) {
    case 'history/fetched':
      return {
        ...state,
        [action.serialNo]: {
          ...state[action.serialNo],
          [action.from]: FetchingState.FETCHED,
        },
      }
    case 'history/fetching':
      return {
        ...state,
        [action.serialNo]: {
          ...state[action.serialNo],
          [action.from]: FetchingState.FETCHING,
        },
      }
    case 'reset': {
      return {}
    }
    default:
      return state
  }
}
const enhancer = composeWithDevTools({ name: 'SystemData' })
const rootReducer = combineReducers({ systemInfo, historyData, forecastPower, breakthrough, systemDataError, fetchState, versionInfo })

export const resetStoreHistoryData = (serialNo: string): void => {
  store?.dispatch({ type: 'reset', serialNo })
}

interface StoreModel {
  systemInfo: SystemInfoState
  historyData: HistoryState
  forecastPower: ForecastState
  breakthrough: BreakthroughState
  systemDataError: ErrorState
  fetchState: FetchState
  versionInfo: VersionState
}

export type StoreType = (EmptyObject & StoreModel) | (EmptyObject & StoreModel & PersistPartial)
let store: Store<StoreType, SystemDataStoreAction | SystemDataStoreActionWithTime | VersionStateAction>
let persistor: Persistor

const migrations: MigrationManifest = {
  //completely clear system data from redux store when version changes to 0
  //add future versions as well to handle changes
  0: state => ({}),
  1: state => ({}),
}

/**
 *  {
 *    1234: {
 *      123: {
 *         data: [                  =>      data: {
 *          { a: 1, b: 2 },                   a: [1, 2],
 *          { a: 2, b:1 }                     b: [2, 1]
 *         ],                               },
 *         powermeters: {           =>      powermeters: {
 *          1: [                              1: { a: [1, 2], b: [2, 1] },
 *            { a: 1, b: 2 },                 2: { a: [1, 2], b: [2, 1] },
 *            { a: 2, b: 1 }                }
 *          ],
 *          2: [
 *            { a: 1, b: 2 },
 *            { a: 2, b: 1 }
 *          ],
 *         },
 *         wallboxes: {             =>      wallboxes: {
 *          1: [                              1: { a: [1, 2], b: [2, 1] },
 *            { a: 1, b: 2 },                 2: { a: [1, 2], b: [2, 1] },
 *            { a: 2, b: 1 }                }
 *          ],
 *          2: [
 *            { a: 1, b: 2 },
 *            { a: 2, b: 1 }
 *          ],
 *         },
 *      },
 *      321 : {
 *        ...
 *      },
 *    },
 *    4321: {
 *      ...
 *    }
 * }
 */
interface SdhData {
  [key: string]: {
    [key: string]: {
      data: { [key: string]: any }[]
      powermeters: { [key: string]: { [key: string]: any }[] }
      wallboxes: { [key: string]: { [key: string]: any }[] }
    }
  }
}

interface ConvertedSdhData {
  [key: string]: {
    [key: string]: {
      data: { [key: string]: any[] }
      powermeters: { [key: string]: { [key: string]: any[] } }
      wallboxes: { [key: string]: { [key: string]: any[] } }
    }
  }
}

export const createEncryptedSystemDataStore = async (
  secret: string | undefined,
  userId: number | string,
  cookiesAccepted: boolean
): Promise<void> => {
  const module = await import('localforage')
  let useCompression = false
  try {
    const estimate = await navigator?.storage?.estimate?.()
    const quota = estimate?.quota
    const usage = estimate?.usage

    if (quota && usage && quota - usage < 20000000) {
      useCompression = true
    }
  } catch (err) {
    console.error(err)
  }
  // if less than 2 MB space is remaining clear the store
  const compressor = createCompressor()
  const SetTransform = createTransform(
    (inboundState: SdhData, key) => {
      const converted: ConvertedSdhData = {}

      const firstLevelKeys = Object.keys(inboundState)

      firstLevelKeys.forEach(key => {
        const secondLevelKeys = Object.keys(inboundState[key])
        secondLevelKeys.forEach(secondKey => {
          const inboundData = inboundState[key][secondKey].data
          const inboundPowermeters = inboundState[key][secondKey].powermeters
          const inboundWallboxes = inboundState[key][secondKey].wallboxes
          const data = inboundData?.length > 0 ? revertObjectApiResult(inboundState[key][secondKey].data) : {}
          const powermeters = Object.keys(inboundPowermeters)?.reduce(
            (prev, powermeter) => ({
              ...prev,
              [powermeter]: inboundPowermeters[powermeter].length > 0 ? revertObjectApiResult(inboundPowermeters[powermeter]) : {},
            }),
            {}
          )
          const wallboxes = Object.keys(inboundWallboxes)?.reduce(
            (prev, wallbox) => ({
              ...prev,
              [wallbox]: inboundWallboxes[wallbox].length > 0 ? revertObjectApiResult(inboundWallboxes[wallbox]) : {},
            }),
            {}
          )

          converted[key] = {
            ...(converted[key] as any),
            [secondKey]: { ...inboundState[key][secondKey], powermeters, wallboxes, data },
          }
        })
      })

      return converted
    },
    (outboundState: ConvertedSdhData, key) => {
      const converted: SdhData = {}

      const firstLevelKeys = Object.keys(outboundState)

      firstLevelKeys.forEach(key => {
        const secondLevelKeys = Object.keys(outboundState[key])
        secondLevelKeys.forEach(secondKey => {
          const outboundPowermeters = outboundState[key][secondKey].powermeters
          const outboundWallboxes = outboundState[key][secondKey].wallboxes
          const data = objectApiResult(outboundState[key][secondKey].data)
          const powermeters = Object.keys(outboundPowermeters)?.reduce(
            (prev, powermeter) => ({
              ...prev,
              [powermeter]: objectApiResult(outboundPowermeters[powermeter]),
            }),
            {}
          )
          const wallboxes = Object.keys(outboundWallboxes)?.reduce(
            (prev, wallbox) => ({
              ...prev,
              [wallbox]: objectApiResult(outboundWallboxes[wallbox]),
            }),
            {}
          )

          converted[key] = {
            ...(converted[key] as any),
            [secondKey]: { ...outboundState[key][secondKey], powermeters, wallboxes, data },
          }
        })
      })

      return converted
    },
    { whitelist: ['historyData'] }
  )
  const persistConfig: PersistConfig = {
    ...(useCompression ? { transforms: [SetTransform, compressor] } : {}),
    blacklist: ['fetchState'],
    key: `systemData-${userId}`,
    storage: module.default,
    version: 1, //NOTE: Change this version to invalidate store based on migrations above
    migrate: createMigrate(migrations, { debug: true }),
    //debug: true,
  }

  if (cookiesAccepted) {
    const persistedReducer = persistReducer(persistConfig, rootReducer)
    store = createStore(persistedReducer, enhancer())
    persistor = persistStore(store)
  } else {
    store = createStore(rootReducer, enhancer())
  }
}

export const getStore = (): typeof store | undefined => {
  return store
}

export const getPersistor = (): Persistor | undefined => {
  return persistor
}
